import sha512 from 'crypto-js/sha512';
import encHex from 'crypto-js/enc-hex';
import fetch from 'isomorphic-fetch';
import _ from 'lodash';
import dataStorage from '../misc/data-storage';
import misc from '../misc/misc';

export default class HttpClient {
  constructor(serviceUrlRoot, responseHandler) {
    this.serviceUrlRoot = serviceUrlRoot;
    this.responseHandler = responseHandler;
  }

  async callService(relUrl, ...args) {
    const userId = dataStorage.hasCredential() ? dataStorage.getUserId() : 0;
    const token = dataStorage.hasCredential() ? dataStorage.getToken() : '';
    const appVersion = dataStorage.getAppVersion();
    const appInstanceId = dataStorage.getAppInstanceId();
    return this._callServiceByFetch(
      relUrl, this._parseCallServiceArgs(...args), userId, token, appVersion, appInstanceId);
  }

  async callServiceForRemoteObject(relUrl, ...args) {
    const responseBody = await this.callService(relUrl, ...args);
    return responseBody.getRemoteObject();
  }

  async callServiceForPagedObjects(relUrl, ...args) {
    const responseBody = await this.callService(relUrl, ...args);
    return responseBody.getPagedObjects();
  }

  async callServiceForRemoteObjects(relUrl, ...args) {
    const responseBody = await this.callService(relUrl, ...args);
    return responseBody.getRemoteObjects();
  }

  async callServiceForObject(relUrl, ...args) {
    const responseBody = await this.callService(relUrl, ...args);
    return responseBody.getRetObject();
  }

  async callServiceForObjects(relUrl, ...args) {
    const responseBody = await this.callService(relUrl, ...args);
    return responseBody.getRetArray();
  }

  async callServiceForString(relUrl, ...args) {
    const responseBody = await this.callService(relUrl, ...args);
    return responseBody.getRetString();
  }

  async callServiceForLong(relUrl, ...args) {
    const responseBody = await this.callService(relUrl, ...args);
    return responseBody.getRetLong();
  }

  async callServiceForBoolean(relUrl, ...args) {
    const responseBody = await this.callService(relUrl, ...args);
    return responseBody.getRetBoolean();
  }

  _createHash(...args) {
    return encHex.stringify(sha512(Array.from(args).join('_')));
  }

  _createRequestHeaders(url, userId, token, appVersion, appInstanceId) {
    // Create headers array.
    const headers = {
      Url: url,
      'App-Version': appVersion,
      'App-Instance-Id': appInstanceId,
      'Content-Type': 'application/json',
    };

    // Headers that apply only to non-public calls.
    if (userId > 0 && token) {
      headers['User-Id'] = `${userId}`;
      headers.Authorization = `Bearer ${this._createHash(token, url)}`;
      // headers.Authorization = `Bearer debug_remote_token_kkk`;
    }
    return headers;
  }

  _createRequestBody(parameters) {
    return {
      parameters,
    };
  }

  _callServiceByFetch(url, parameters = {}, userId = 0, token = '', appVersion = 0, appInstanceId = '') {
    // Create the http request body.
    const requestBody = this._createRequestBody(parameters);

    // Create http request headers.
    const headers = this._createRequestHeaders(url, userId, token, appVersion, appInstanceId);
    headers['Content-Length'] = requestBody.length;

    // Set the fetch url & options.
    const fetchUrl = this.serviceUrlRoot + url;
    const fetchOptions = {
      url: fetchUrl,
      method: 'POST',
      // eslint-disable-next-line object-shorthand
      headers: headers,
      body: JSON.stringify(requestBody),
    };

    // Make the request and return a promise.
    const fetchPromise = fetch(fetchUrl, fetchOptions);

    // Handle the fetch promise with default handling, and return a promise for service response.
    return this.responseHandler.handleFetchPromise(fetchPromise);
  }

  /**
   * Converts input arguments for service calls to a parameters object to be sent to backend server.
   */
  _parseCallServiceArgs(...args) {
    let argObj = this._convertCallServiceArgsToObject(...args);

    // Convert object keys to snake case, which is needed by backend.
    argObj = misc.objectKeysToSnakeCase(argObj);

    // Convert array type values to strings by joining them by ','.
    _.forEach(argObj, (value, key) => {
      if (_.isArray(value)) {
        argObj[key] = value.join(',');
      }
    });

    return argObj;
  }

  _convertCallServiceArgsToObject(...args) {
    if (args.length === 0) {
      return {};
    } else if (args.length === 1) {
      return args[0];
    } else {
      const parameters = {};
      for (let i = 0; i < args.length; i += 2) {
        parameters[args[i]] = args[i + 1];
      }
      return parameters;
    }
  }
}
